// Phase 0a — read-only Places of Performance tab. Same pattern as CLINsTab.
// S11a adds a per-state work rollup above the per-place table; S11b adds a
// per-state employee owner ("region owner") to that rollup.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useMemo } from 'react';
import { ContactPicker } from '../../_shared/components/ContactPicker';
import { usePlacesOfPerformance, useRegionOwners, useSetRegionOwner } from '../contractHooks';
// S11a/S11b — roll the program's work up by state: for each state, the summed
// percentage of work, how many places sit in it (largest share first), and the
// contact who owns work there. The rollup is a pure FE derivation over the
// places the table below lists; the owner is a small ryugu/pangaea entity keyed
// on (program_id, state). A place with no state is bucketed under "—".
function StateBreakdown({ places, programId, truncated }) {
    const { data: ownersData } = useRegionOwners(programId);
    const setOwner = useSetRegionOwner(programId);
    // Group by a canonical (upper-cased, trimmed) state so "VA" and "va" fold into
    // one region — otherwise they split the work % and expose two owners for one
    // place. The map key IS the owner key the picker writes and reads under
    // (below), so rollup, display, and ownership stay in lockstep. `label` keeps
    // the first-seen original casing so a 2-letter code stays clean and a full
    // name like "Virginia" isn't shouted.
    const byState = useMemo(()=>{
        const m = new Map();
        for (const p of places){
            const raw = p.state.trim();
            const key = raw.toUpperCase() || '—';
            const cur = m.get(key) ?? {
                label: raw || '—',
                pct: 0,
                count: 0
            };
            cur.pct += p.percentage;
            cur.count += 1;
            m.set(key, cur);
        }
        return [
            ...m.entries()
        ].sort((a, b)=>b[1].pct - a[1].pct);
    }, [
        places
    ]);
    const ownerByState = useMemo(()=>{
        const m = new Map();
        for (const o of ownersData?.regionOwners ?? []){
            // Key by the same canonical form so an owner persisted under any casing
            // matches its rollup row.
            m.set(o.state.trim().toUpperCase(), {
                ownerContactId: o.ownerContactId,
                ownerName: o.ownerName
            });
        }
        return m;
    }, [
        ownersData
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "text-sm font-medium text-muted-foreground",
                children: [
                    "Work by state",
                    truncated && /*#__PURE__*/ _jsx("span", {
                        className: "ml-1 font-normal text-amber-700 dark:text-amber-300",
                        children: "(first page only — totals partial until Phase 0b pagination)"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto rounded-md border",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full text-sm",
                    "data-testid": Testids.deliveryOperational.placesStateTable,
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "bg-muted/40",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                className: "text-left",
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium",
                                        children: "State"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium text-right",
                                        children: "% of work"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium text-right",
                                        children: "Places"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium",
                                        children: "Owner"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: byState.map(([state, { label, pct, count }])=>{
                                const owner = ownerByState.get(state);
                                return /*#__PURE__*/ _jsxs("tr", {
                                    "data-testid": Testids.deliveryOperational.placesStateRow,
                                    className: "border-t hover:bg-muted/20",
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2",
                                            children: label
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2 text-right",
                                            children: fmtPercentage(pct)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2 text-right",
                                            children: count
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2",
                                            children: state === '—' ? // Places with no state bucket here; there is no region to
                                            // own, so don't offer an owner (avoids persisting an owner
                                            // keyed on the "—" placeholder glyph).
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-muted-foreground",
                                                children: "—"
                                            }) : /*#__PURE__*/ _jsx(ContactPicker, {
                                                id: `region-owner-${state}`,
                                                label: `Owner for ${label}`,
                                                hideLabel: true,
                                                programId: programId,
                                                value: owner?.ownerContactId ?? '',
                                                fallbackLabel: owner?.ownerName,
                                                onChange: (contactId, name)=>setOwner.mutate({
                                                        state,
                                                        ownerContactId: contactId,
                                                        ownerName: name
                                                    }),
                                                // Clear resets the row to unowned (empty id + name), so a
                                                // misassignment — or an owner who has left — can be undone.
                                                // The backend Upsert nulls the owner in place.
                                                onClear: ()=>setOwner.mutate({
                                                        state,
                                                        ownerContactId: '',
                                                        ownerName: ''
                                                    }),
                                                testid: Testids.deliveryOperational.regionOwnerPicker
                                            })
                                        })
                                    ]
                                }, state);
                            })
                        })
                    ]
                })
            })
        ]
    });
}
// extractErrorMessage handles Connect's ConnectError (which has a useful
// `.message`) plus plain Error and primitive throws.
function extractErrorMessage(err) {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    try {
        return JSON.stringify(err);
    } catch  {
        return 'Unknown error';
    }
}
// fmtPercentage never collapses zero to '—' — `0%` is a meaningful split
// (e.g. observer location) and proto3 has no nullable numeric.
function fmtPercentage(v) {
    return `${v.toFixed(0)}%`;
}
export function PlacesOfPerformanceTab({ programId }) {
    const { data, isLoading, error } = usePlacesOfPerformance(programId);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-muted-foreground",
            children: "Loading places of performance…"
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "text-destructive",
            children: [
                "Failed to load places: ",
                extractErrorMessage(error)
            ]
        });
    }
    const places = data?.places ?? [];
    if (places.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-muted-foreground",
            children: "No places of performance recorded for this program yet."
        });
    }
    const total = data?.total ?? BigInt(0);
    const shownCount = BigInt(places.length);
    const truncated = total > shownCount;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            truncated && /*#__PURE__*/ _jsxs("div", {
                className: "text-sm text-amber-700 dark:text-amber-300",
                children: [
                    "Showing first ",
                    shownCount.toString(),
                    " of ",
                    total.toString(),
                    " places of performance. Full pagination lands in Phase 0b."
                ]
            }),
            /*#__PURE__*/ _jsx(StateBreakdown, {
                places: places,
                programId: programId,
                truncated: truncated
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto rounded-md border",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full text-sm",
                    "data-testid": Testids.deliveryOperational.placesTable,
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "bg-muted/40",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                className: "text-left",
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium",
                                        children: "Primary"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium",
                                        children: "Description"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium text-right",
                                        children: "%"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium",
                                        children: "City"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium",
                                        children: "State"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium",
                                        children: "ZIP"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-3 py-2 font-medium",
                                        children: "Country"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: places.map((p)=>/*#__PURE__*/ _jsxs("tr", {
                                    "data-testid": Testids.deliveryOperational.placeRow,
                                    className: "border-t hover:bg-muted/20",
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2",
                                            children: p.primary ? '★' : ''
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2",
                                            children: p.description || '—'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2 text-right",
                                            children: fmtPercentage(p.percentage)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2",
                                            children: p.city || '—'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2",
                                            children: p.state || '—'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2 font-mono",
                                            children: p.zipCode || '—'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2",
                                            children: p.country || '—'
                                        })
                                    ]
                                }, p.id))
                        })
                    ]
                })
            })
        ]
    });
}
